import { confirmDialog } from '../../ui/feedback';
import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import Button from '../../ui/Button/Button';
import Badge from '../../ui/Badge';
import Icon from '../../ui/Icon';
import { ICONS } from '../../ui/icon-map';
import Tabs from '../../ui/Tabs/Tabs';
import RequirePermission from '../../components/RequirePermission';
import { usePermissions } from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../constants/permissions';
import { finesService, errorMessage } from '../../services/finesService';
import OverviewTab from './components/OverviewTab';
import FinesTab from './components/FinesTab';
import DaysTab from './components/DaysTab';
import RunsTab from './components/RunsTab';
import FilesTab from './components/FilesTab';
import AutoEjarCard from '../the-engine/AutoEjarCard';

const TABS = [
    { id: 'overview', label: 'Overview', icon: ICONS.reports },
    { id: 'fines', label: 'Fines', icon: ICONS.rta },
    { id: 'days', label: 'By day', icon: ICONS.calendar },
    { id: 'runs', label: 'Processing', icon: ICONS.robot, permission: PERMISSIONS.run_traffic_fines },
    // everyone on the page may download the reports (the engine's /api/export/* rule checks its own permission);
    // nothing is uploaded here any more (owner, 28 Sep 2026)
    { id: 'files', label: 'Reports', icon: ICONS.download },
];

// a link may open the page on a tab with a filter (owner, 3 Oct 2026: the Overview's "Needs attention" must lead to the
// fines themselves): /traffic-fines?tab=fines&match=unassigned / &review=1 / &status=Unpaid
const LINK_FILTERS = ['q', 'branch', 'status', 'match', 'review', 'source', 'has_image', 'impound', 'tab_name', 'date_from', 'date_to'];
const fromLink = () => {
    const p = new URLSearchParams(window.location.search);
    const f = {};
    LINK_FILTERS.forEach((k) => { if (p.get(k)) f[k === 'tab_name' ? 'tab' : k] = p.get(k); });
    return { tab: p.get('tab') || (Object.keys(f).length ? 'fines' : 'overview'), filters: f };
};

export default function TrafficFines() {
    const [tab, setTab] = useState(() => fromLink().tab);
    const [filters, setFilters] = useState(() => fromLink().filters);
    const [engineOk, setEngineOk] = useState(null);
    const [notice, setNotice] = useState(null);
    const [busy, setBusy] = useState(false);
    // isAdmin: the Admin role as the checks see it ("show the app as a role")
    const { hasPermission, isAdmin } = usePermissions();
    const tabItems = TABS
        .filter((t) => !t.permission || hasPermission(t.permission))
        .map((t) => ({ id: t.id, label: t.label, icon: <Icon glyph={t.icon} size="sm" /> }));

    useEffect(() => {
        finesService.health().then(() => setEngineOk(true)).catch(() => setEngineOk(false));
    }, []);
    // a link's tab must be one this user has - else the page opens on its overview
    const tabIds = tabItems.map((t) => t.id).join(',');
    useEffect(() => {
        if (!tabIds.split(',').includes(tab)) setTab('overview');
    }, [tab, tabIds]);

    // Overview cards open the Fines tab with a filter applied.
    const openFines = useCallback((f) => { setFilters(f || {}); setTab('fines'); }, []);

    // Heavy work always asks first (a slip of the mouse must not start a portal run)
    const syncPortal = async () => {
        if (!(await confirmDialog({
            title: 'Sync from RTA now?',
            message: 'Reads the whole fines list from the RTA portal, then matches, payments and images. Takes a few minutes; background runs wait meanwhile.',
            confirmLabel: 'Yes, sync from RTA',
        }))) return;
        setBusy(true); setNotice(null);
        try {
            await finesService.portalRun();
            setNotice({ tone: 'ok', text: 'RTA sync started: reading the fines list from the portal, then matching, payments and images.' });
            setTab('runs');
        } catch (e) {
            const msg = errorMessage(e);
            const running = /progress/i.test(msg);
            setNotice({ tone: running ? 'warn' : 'err', text: running ? 'A run is already in progress, see the Processing tab.' : msg });
        } finally { setBusy(false); }
    };

    const processNow = async () => {
        if (!(await confirmDialog({
            title: 'Match fines to Ejar contracts now?',
            message: 'Takes the latest Ejar contract exports, gives every fine to the contract that had the car at that minute, detects payments and fetches missing images.',
            confirmLabel: 'Yes, match now',
        }))) return;
        setBusy(true); setNotice(null);
        try {
            await finesService.quickRun();
            setNotice({ tone: 'ok', text: 'Matching started: Ejar contracts, payments, missing images - see the Processing tab.' });
            setTab('runs');
        } catch (e) {
            const msg = errorMessage(e);
            const running = /progress/i.test(msg);
            setNotice({ tone: running ? 'warn' : 'err', text: running ? 'A run is already in progress, see the Processing tab.' : msg });
        } finally { setBusy(false); }
    };

    const headerActions = (
        <>
            {engineOk === false && <Badge variant="danger" dot>Fines engine is offline ({finesService.baseUrl})</Badge>}
            {engineOk && <Badge variant="success" dot>Engine online</Badge>}
            <RequirePermission permission={PERMISSIONS.run_traffic_fines}>
                <Button leadingIcon={<Icon glyph={ICONS.rta} size="sm" />} onClick={syncPortal} isLoading={busy} disabled={busy || engineOk === false} title="Read the fines list from the RTA portal now (runs by itself every morning)">
                    Sync from RTA
                </Button>
                <Button variant="primary" leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />} onClick={processNow} isLoading={busy} disabled={busy || engineOk === false}
                    title="Match every fine to the Ejar contract that had the car at that minute (Ejar contract exports), then payments and images">
                    Match to Ejar contracts
                </Button>
            </RequirePermission>
        </>
    );

    const noticeCls = notice?.tone === 'ok'
        ? 'border-green-200 bg-green-50 text-green-800'
        : notice?.tone === 'warn' ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-red-200 bg-red-50 text-red-700';

    return (
        <Layout title="Traffic Fines" subtitle="RTA fines matching, images & follow-up" backPath="/" actions={headerActions}>
            <div className="w-full">          {/* the whole screen width: the fines table has many columns */}
                <Tabs items={tabItems} value={tab} onChange={setTab} label="Traffic fines sections" idPrefix="fines-tab" className="mb-4" />
                {notice && (
                    <div role={notice.tone === 'ok' ? 'status' : 'alert'} className={`mb-4 px-4 py-2 rounded-lg border text-sm ${noticeCls}`}>
                        {notice.text}
                    </div>
                )}
                <div role="tabpanel" id={`fines-tab-panel-${tab}`} aria-labelledby={`fines-tab-${tab}`}>
                    {tab === 'overview' && <OverviewTab onOpenFines={openFines} />}
                    {tab === 'fines' && (
                        <div className="flex flex-col gap-3">
                            <AutoEjarCard kind="fines" isAdmin={isAdmin} />
                            <FinesTab initialFilters={filters} />
                        </div>
                    )}
                    {tab === 'days' && <DaysTab />}
                    {tab === 'runs' && <RunsTab />}
                    {tab === 'files' && <FilesTab />}
                </div>
            </div>
        </Layout>
    );
}
